import type { CoachSuggestion } from '../../domain/coach';
import { renderTeamProfile } from '../../domain/teamProfile';
import type { TeamProfileFacts } from '../../domain/teamProfile';
import { freshnessLabel } from '../freshness';
import { cardPagesB } from '../card/cardB';
import { ARTIFACT_PHASE } from '../../types';
import type {
  AdjustmentEntry,
  AdjustmentStatus,
  CoachCardContext,
  CoachCardRow,
  HypothesisPhase,
  MatchPurpose,
  MatchRow,
  OurPlayerRow,
  TeamShape,
} from '../../types';
import type { CoachFailureCode, CoachRequest, WorkflowEntry } from '../../app/coach';

/**
 * B-15 — the **adjustments workflow's pure decisions** (design spec §2.5; §5.2/§5.4; scenarios
 * 22–28). DOM-free so the Node suite drives every arm; `useWorkflowB` is glue over these plus
 * B-3's `app/coach` facade (the draft commands) and the engine's own reads.
 *
 * Three things live here because each is a rule a screenshot cannot check:
 *
 * 1. **§5.2's request, and everything it does not carry.** `buildCoachRequestB` is the one
 *    constructor for the payload, so scenario 24's negative ("not sent: audio, voice notes,
 *    opponent identities, match notes, set-piece diagrams") is a property of one function
 *    rather than a habit of several call sites.
 * 2. **§5.4's taxonomy, as copy.** A `Record` over the five codes: a code without copy is a
 *    compile error, which is the mechanical form of "a code with no surface is a defect".
 * 3. **The entry lifecycle.** Suggest → accept / edit / reject, and manual entries beside them,
 *    with `aiOriginalText` retained on the first edit. What renders on the card is **never
 *    restated here**: `renderableCountB` counts B-16's own pages, which are the engine handler's
 *    exported `rendersOnCard` — so the "(n)" on the finalize button, the pages the card shows and
 *    the guard that refuses an empty card are one predicate with three readers.
 */

// ── §5.2's request ───────────────────────────────────────────────────────────

/**
 * Scenario 24 / §5.2 — does this (purpose, context) send the team profile?
 *
 * *"team match (both moments) AND advance-scout full time"* — which leaves exactly one arm out,
 * and that arm has no workflow at all (scenario 20; the engine rejects it as
 * `ERR_CARD_CONTEXT`). Written as the rule rather than as `true`, because the rule is what the
 * verification asserts and what a future fourth framing would have to answer to.
 *
 * **The rule is kept verbatim from `includeRosterB`; only the name moved** (§18.4), because §18
 * widened what travels from a roster to a whole profile — roster *and* shape — and a predicate
 * called `includeRosterB` gating a shape would be a name that lies.
 */
export function includeTeamProfileB(
  purpose: MatchPurpose | null,
  context: CoachCardContext,
): boolean {
  if (purpose !== 'advanceScout') return true;
  return context === ARTIFACT_PHASE.fullTime;
}

/**
 * §5.2 — build the whole request body, and nothing else.
 *
 * Selections keep the **entries' own order**, which is `loadWorkflowContext`'s DISC-4 collation
 * (count desc; ties by first observation, then creation) — §5.2 says "the user's Step-1
 * selection, DISC-4 order", and re-sorting here would be a second ranking to disagree with the
 * first. Annotations ride along as their text lines, oldest first, exactly as the detail sheet
 * shows them.
 *
 * The roster is filtered to **active** players: scenario 2's purpose is that suggestions fit the
 * personnel actually available, and an inactive player is by definition not available. §17.1 makes
 * that filter's consequence visible on the screen (*"(2) — not sent to Coach Claude"*), which is
 * why it is applied here at the payload rather than hidden inside the renderer.
 *
 * **`team` is prose, composed by `domain/teamProfile.renderTeamProfile` and by nothing else**
 * (§18.1). Its `null` is load-bearing: it is exactly the disclosure's `'withheld'` and `'empty'`
 * answers, and §18.5's biconditional is what pins the two together.
 */
export function buildCoachRequestB(input: {
  readonly match: MatchRow;
  readonly context: CoachCardContext;
  readonly entries: readonly WorkflowEntry[];
  readonly selectedHypothesisIds: readonly string[];
  readonly roster: readonly OurPlayerRow[];
  readonly shape: TeamShape;
}): CoachRequest {
  const selected = new Set(input.selectedHypothesisIds);
  return {
    format: input.match.format,
    purpose: input.match.purpose,
    context: input.context,
    selections: input.entries
      .filter((entry) => selected.has(entry.hypothesis.id))
      .map((entry) => ({
        hypothesisId: entry.hypothesis.id,
        text: entry.hypothesis.text,
        phase: entry.hypothesis.phase,
        count: entry.hypothesis.cachedCount,
        annotations: entry.annotations.map((note) => note.text),
      })),
    team: includeTeamProfileB(input.match.purpose, input.context)
      ? renderTeamProfile(
          input.roster.filter((player) => player.active),
          input.shape,
        )
      : null,
  };
}

// ── §5.6's disclosure, as copy (build unit B-22) ─────────────────────────────

/**
 * What the request will actually do with the team profile — the three answers, and they are the
 * only three {@link buildCoachRequestB} can produce.
 */
export type RosterDisclosureB =
  /** The profile travels: the rule allows it and there is something to say (a player or a shape). */
  | 'sending'
  /** The rule allows it and the user has recorded nothing — §3.1.2's input is simply missing. */
  | 'empty'
  /** Scenario 24's rule withholds it for this (purpose, moment) regardless of the profile. */
  | 'withheld';

/**
 * **B-22 — the disclosure decided from the request, not from a habit.**
 *
 * The milestone review found `AdjustmentsWorkflowB`'s disclosure saying it sends *"your team
 * descriptions"* whether or not any exist, while `buildCoachRequestB` sent `roster: []` silently.
 * Requirements §3.1.2 gives the roster its purpose — *"This roster is part of what Coach Claude
 * sees, **so suggestions fit the personnel actually available**"* — and B starts with an empty
 * database (requirements §1), so on the first real team match the flagship feature could run
 * personnel-blind with no symptom but suggestions that feel generic. Which reads as the model being
 * mediocre rather than as a missing input, and that is the whole defect.
 *
 * **This is a copy fix and nothing more.** An empty roster is legal and the generate is never
 * blocked — the surface says what is missing and where to fix it, and sends.
 *
 * The two rules below are `buildCoachRequestB`'s own, in the same order, and that is the point:
 * the sentence the user reads and the payload that leaves are one decision. `workflowB.test.ts`
 * asserts the biconditional (`'sending'` iff the built request carries a profile) rather than
 * asserting this function's branches, because two functions agreeing today is not a mechanism.
 *
 * **§18.5 restates the biconditional rather than dropping it:**
 *
 * > `rosterDisclosureB(...) === 'sending'` **iff** `request.team !== null`
 *
 * which is why `'empty'` **widens** to *no active players **and** no shape*. The shape's free text
 * is user-authored prose about the team and is subject to the same residual, so a profile that is
 * nothing but a 3-3-2 still travels — and must still be disclosed. Deciding it by calling the same
 * `renderTeamProfile` the payload calls is what makes the two sides one decision instead of two
 * conditions that happen to match.
 */
export function rosterDisclosureB(
  purpose: MatchPurpose | null,
  context: CoachCardContext,
  roster: readonly OurPlayerRow[],
  shape: TeamShape,
): RosterDisclosureB {
  if (!includeTeamProfileB(purpose, context)) return 'withheld';
  const profile = renderTeamProfile(
    roster.filter((player) => player.active),
    shape,
  );
  return profile === null ? 'empty' : 'sending';
}


/**
 * §5.6's disclosure line, per answer. **It never claims to send what it is not sending** — the
 * clause about the team profile is present in exactly the case where it travels.
 *
 * `empty` and `withheld` carry the same sentence deliberately: from the user's side they are the
 * same fact (no team profile leaves this device), and only the *reason* differs. The reason is
 * what {@link ROSTER_EMPTY_NOTE} adds, and it is added only where the user can act on it.
 *
 * ── §18.5 widened this from three strings to three functions ────────────────
 *
 * *"your team descriptions"* becomes **"your team profile"** — the profile is now a roster *and* a
 * shape, and the old phrase named half of what leaves. The `sending` line also gains the **count**
 * and the **freshness stamp** (§18.5), which a bare string cannot carry.
 *
 * Still a `Record` over the closed union, and that is the mechanism worth keeping: an answer added
 * without copy is a **compile error**, which is the mechanical form of *"an answer with no surface
 * is a defect"*. The value type widened; the exhaustiveness did not move.
 *
 * ── And B-1 widened it again, to `(facts, now)` ─────────────────────────────
 *
 * The stamp needs today's date to know whether to print the year (`ui-b/freshness.ts`), and the
 * clock is read at the call site rather than inside the label — the project's form everywhere else.
 * `empty` and `withheld` ignore the argument, which is the cost of keeping one uniform signature
 * across the `Record`; the alternative is three different shapes and no exhaustiveness.
 */
export const DISCLOSURE_COPY: Record<
  RosterDisclosureB,
  (facts: TeamProfileFacts, now: number) => string
> = {
  sending: (facts, now) => {
    const stamp = freshnessLabel(facts.lastChanged, now);
    const players = `${String(facts.activeCount)} of ${String(facts.totalCount)} player${facts.totalCount === 1 ? '' : 's'}`;
    const profile = stamp === null ? `${players} and your shape` : `${players} and your shape, last changed ${stamp}`;
    return `Sends your selected patterns, their counts and your annotations, plus your team profile — ${profile}. No audio, no opponent names, no match notes.`;
  },
  empty: () =>
    'Sends your selected patterns, their counts and your annotations to Coach Claude. No audio, no opponent names, no match notes.',
  withheld: () =>
    'Sends your selected patterns, their counts and your annotations to Coach Claude. No audio, no opponent names, no match notes.',
};

/**
 * The one sentence the empty case adds: the consequence, then where to fix it.
 *
 * Named, not a `'withheld'` variant, because the two are different offers — nothing the user can do
 * on this screen changes scenario 24's rule, and telling them to go and add players would be advice
 * that does not work.
 */
export const ROSTER_EMPTY_NOTE =
  'My Team is empty, so Coach Claude won’t know which players you have. Add them in the My Team tab.';

/**
 * B-30 / §20 — the refusal when a generate has nowhere to land.
 *
 * `useWorkflowB` does not issue the request at all when the card is already full: spending a model
 * call whose result cannot be committed is simply wrong, and the shipped commit seam would slice
 * the answer to nothing. It is a **refusal with a fix**, which is why it names the action — the
 * user removes an adjustment and presses again.
 *
 * Deliberately NOT a disabled button. §26's break row and this project's own lesson both say a
 * blocked action is proved by racing it, not by pressing a control the surface greyed out: the
 * guard lives in the workflow, so pressing *Ask Coach Claude* on a full card demonstrably makes no
 * request and produces this sentence.
 */
export const CARD_FULL_NOTE = 'The card is full. Remove an adjustment to make room.';

/**
 * B-30 / §20 — *"Coach Claude returned 24 — the first 20 are here."*, or `null` when it did not.
 *
 * §3.8.43 stopped the Worker stating any number to the model, so a response longer than the card
 * is now reachable — and `validateCoachSuggestions` truncates it by `break`, **silently**. Silence
 * is the one thing that scenario cannot have: quietly trimming the menu is the exact failure
 * *"i want to order from a bigger, better menu"* exists to end.
 *
 * **This is a report of what happened, not a warning about allocation.** It says nothing about
 * capacity, offers no advice, and never blocks; it appears once, on the answer it describes.
 *
 * The condition is truncation itself, not merely "fewer survived than arrived". Entries also drop
 * for a blank text, an over-long text or an unknown phase, and *"the first 20 are here"* would be a
 * lie about those — they were not the first anything. Truncation is exactly `kept === cap` while
 * more than `cap` arrived: the validator's `break` can only have fired with entries left unread.
 *
 * `kept === cap` rather than `kept >= cap`, deliberately. `kept` can never exceed the cap the
 * validator enforced — but if a caller ever passed a *different* cap from the one that did the
 * enforcing (the drift class this whole unit exists to remove), the loose form would print *"the
 * first 25 are here"* under a ceiling of twenty: a sentence that contradicts itself. Silence is the
 * right answer to an input that cannot be described honestly.
 */
export function truncationNoticeB(returned: number, kept: number, cap: number): string | null {
  if (kept !== cap || returned <= cap) return null;
  return `Coach Claude returned ${String(returned)} — the first ${String(kept)} are here.`;
}

// ── §5.4's taxonomy, as copy ─────────────────────────────────────────────────

/** What one failure code puts on screen (§5.4's "leads with" and "then" columns). */
export interface FailureCopyB {
  /** The sentence the user reads first — plain words, never a code or a status number. */
  readonly lead: string;
  /** Why the manual path is being offered, in the same breath. */
  readonly then: string;
  /** §5.4 — a single Retry button, on the two codes where retrying can plausibly differ. */
  readonly retry: boolean;
  /** §5.4 — point at More → service credential, on the two codes that are about the credential. */
  readonly credentialHint: boolean;
}

/**
 * §5.4's table, verbatim in intent.
 *
 * Every code renders its own sentence and **prompts straight into manual entry** — that is not a
 * per-code decision, it is the whole taxonomy's shared consequence (scenario 27: same card
 * either way, AI is an enhancement and never a gate), so `then` differs in wording and never in
 * whether the offer is made.
 *
 * There is no automatic retry anywhere: `retry` renders a button the user presses, once per
 * press. A break-bounded flow that retried itself would spend the break.
 */
export const FAILURE_COPY: Record<CoachFailureCode, FailureCopyB> = {
  notConfigured: {
    lead: 'Coach Claude isn’t set up yet.',
    then: 'Write the adjustments yourself below — the card is exactly the same either way.',
    retry: false,
    credentialHint: true,
  },
  unreachable: {
    lead: 'Coach Claude isn’t reachable — likely no signal here.',
    then: 'Write the adjustments yourself below — the card is exactly the same either way.',
    retry: false,
    credentialHint: false,
  },
  refused: {
    lead: 'The service refused the request.',
    then: 'Check the service credential in More when you can. Write the adjustments below in the meantime.',
    retry: false,
    credentialHint: true,
  },
  serverError: {
    lead: 'Coach Claude had a problem.',
    then: 'Try once more, or write the adjustments yourself below.',
    retry: true,
    credentialHint: false,
  },
  badResponse: {
    lead: 'Coach Claude answered something unusable.',
    then: 'Try once more, or write the adjustments yourself below.',
    retry: true,
    credentialHint: false,
  },
};

// ── The entry lifecycle (§2.5 step 3; scenarios 25–26) ───────────────────────

/** §2.5 — an AI suggestion, as it enters the draft: *suggested*, never pre-accepted. */
export function aiEntryB(
  suggestion: CoachSuggestion,
  entryId: string,
  at: number,
): AdjustmentEntry {
  return {
    entryId,
    text: suggestion.text,
    phase: suggestion.phase,
    sourceHypothesisIds: [...suggestion.sourceHypothesisIds],
    origin: 'ai',
    status: 'suggested',
    aiOriginalText: null,
    createdAt: at,
  };
}

/**
 * §2.5 — the user's own adjustment. `status: 'accepted'` because there is no one else to accept
 * it: acceptance is the distinct persisted fact that separates an AI suggestion from an adopted
 * one, and a manual entry has no such gap to cross (the engine's `rendersOnCard` reads it the
 * same way). Sources are optional — scenario 26 allows freehand entry with the AI never called.
 */
export function manualEntryB(
  input: {
    readonly text: string;
    readonly phase: HypothesisPhase;
    readonly sourceHypothesisIds: readonly string[];
  },
  entryId: string,
  at: number,
): AdjustmentEntry {
  return {
    entryId,
    text: input.text,
    phase: input.phase,
    sourceHypothesisIds: [...input.sourceHypothesisIds],
    origin: 'manual',
    status: 'accepted',
    aiOriginalText: null,
    createdAt: at,
  };
}

/** §2.5 — accept or reject one entry. A rejected entry is **kept in the row**, not deleted. */
export function setEntryStatusB(entry: AdjustmentEntry, status: AdjustmentStatus): AdjustmentEntry {
  return { ...entry, status };
}

/**
 * §2.5 — save an edit. Two facts land together, and both are requirement 26's:
 *
 * - **the original is retained** (`aiOriginalText`), stamped on the FIRST edit only, so a second
 *   edit cannot overwrite Coach Claude's words with the user's own earlier draft;
 * - **an edited suggestion is an accepted one** — *"accepted text starts as Coach Claude's and
 *   is tweakable"*. Leaving an edited entry at `suggested` would drop the user's own sentence
 *   off the card while showing it on screen, which is the worst available outcome.
 */
export function editEntryB(
  entry: AdjustmentEntry,
  text: string,
  phase: HypothesisPhase,
  sourceHypothesisIds: readonly string[],
): AdjustmentEntry {
  return {
    ...entry,
    text,
    phase,
    sourceHypothesisIds: [...sourceHypothesisIds],
    status: entry.status === 'rejected' ? entry.status : 'accepted',
    aiOriginalText:
      entry.origin === 'ai' && entry.aiOriginalText === null ? entry.text : entry.aiOriginalText,
  };
}

/** Whole-row replacement of one entry — the interaction-grain commit's payload (§2.5). */
export function replaceEntryB(card: CoachCardRow, next: AdjustmentEntry): CoachCardRow {
  return {
    ...card,
    adjustments: card.adjustments.map((entry) =>
      entry.entryId === next.entryId ? next : entry,
    ),
  };
}

/** §2.5 — remove an entry the user wrote and then thought better of. */
export function removeEntryB(card: CoachCardRow, entryId: string): CoachCardRow {
  return { ...card, adjustments: card.adjustments.filter((entry) => entry.entryId !== entryId) };
}

/**
 * Scenario 23 — toggle one selection. **There is no cap here and no cap anywhere**: zero to all,
 * and the absence of a limit is asserted in verification rather than promised in prose (the old
 * three-talk-point limit does not exist in B). Order follows the DISC-4 list the surface
 * renders, so the request's order is the screen's order.
 */
export function toggleSelectionB(
  selected: readonly string[],
  hypothesisId: string,
  ordered: readonly string[],
): string[] {
  const next = new Set(selected);
  if (next.has(hypothesisId)) next.delete(hypothesisId);
  else next.add(hypothesisId);
  return ordered.filter((id) => next.has(id));
}

/**
 * Scenario 29's "(n)" — how many entries would actually render on the card.
 *
 * The count is `cardPagesB(...).length` — literally the pages B-16 renders, which are themselves
 * the engine handler's own `rendersOnCard`. One rule, three readers: the button's number, the
 * card's pages, and the guard that refuses an empty card (`ERR_CARD_EMPTY`). A button that says
 * *(3)* over a two-page card is not a possible state.
 */
export function renderableCountB(card: CoachCardRow | null): number {
  return cardPagesB(card).length;
}

/**
 * §4.2 — is there room for another entry? The UI disables *Add* at the cap with a count note;
 * `ERR_CARD_TOO_LARGE` remains the backstop for a surface that forgot.
 */
export function canAddEntryB(card: CoachCardRow | null, cap: number): boolean {
  return (card?.adjustments.length ?? 0) < cap;
}
